import React, { useEffect, useState } from "react";
import { Box, Card, Container, Skeleton, useTheme } from "@mui/material";

const COLUMN_TEMPLATE =
  "minmax(100px, 1fr) minmax(180px, 1.5fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(100px, 1fr)";
const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 40;
const FOOTER_HEIGHT = 52;
const GRID_HEIGHT = "calc(100vh - 20px)";

function getVisibleRowCount() {
  if (typeof window === "undefined") return 20;
  const body = window.innerHeight - 20 - HEADER_HEIGHT - FOOTER_HEIGHT;
  return Math.max(8, Math.floor(body / ROW_HEIGHT));
}

const SecurityMasterSkeleton = () => {
  const theme = useTheme();
  const [rowCount, setRowCount] = useState(getVisibleRowCount);

  useEffect(() => {
    const onResize = () => setRowCount(getVisibleRowCount());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <Box
      sx={{
        backgroundColor:
          theme.palette.mode === "dark" ? theme.palette.grey[50] : "#ebebeb",
        background:
          theme.palette.mode === "dark" ? theme.palette.grey[50] : "#ebebeb",
        minHeight: "100vh",
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={220} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={420} height={24} />
        </Box>

        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Skeleton
              variant="rectangular"
              width={300}
              height={40}
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <Box sx={{ height: GRID_HEIGHT, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: COLUMN_TEMPLATE,
                alignItems: "center",
                height: HEADER_HEIGHT,
                px: 1,
                backgroundColor: theme.palette.grey[100],
                borderBottom: `2px solid ${theme.palette.divider}`,
                flexShrink: 0,
              }}
            >
              <Skeleton variant="text" width={48} height={18} sx={{ mx: 1 }} />
              <Skeleton variant="text" width={100} height={18} sx={{ mx: 1 }} />
              <Skeleton variant="text" width={52} height={18} sx={{ mx: 1 }} />
              <Skeleton variant="text" width={52} height={18} sx={{ mx: 1 }} />
              <Skeleton variant="text" width={40} height={18} sx={{ mx: 1 }} />
              <Skeleton variant="text" width={40} height={18} sx={{ mx: 1 }} />
              <Skeleton variant="text" width={72} height={18} sx={{ mx: 1 }} />
              <Skeleton variant="text" width={56} height={18} sx={{ mx: 1 }} />
            </Box>

            <Box sx={{ flex: 1, overflow: "hidden" }}>
              {Array.from({ length: rowCount }).map((_, row) => (
                <Box
                  key={row}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: COLUMN_TEMPLATE,
                    alignItems: "center",
                    height: ROW_HEIGHT,
                    px: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Skeleton variant="text" width={48} height={16} sx={{ mx: 1 }} />
                  <Skeleton variant="text" width="80%" height={16} sx={{ mx: 1 }} />
                  <Skeleton variant="text" width={72} height={16} sx={{ mx: 1 }} />
                  <Skeleton variant="text" width={64} height={16} sx={{ mx: 1 }} />
                  <Skeleton variant="text" width={88} height={16} sx={{ mx: 1 }} />
                  <Skeleton variant="text" width={96} height={16} sx={{ mx: 1 }} />
                  <Skeleton variant="text" width={56} height={16} sx={{ mx: 1 }} />
                  <Skeleton variant="text" width={48} height={16} sx={{ mx: 1 }} />
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                height: FOOTER_HEIGHT,
                px: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
              }}
            >
              <Skeleton variant="text" width={110} height={20} />
              <Skeleton variant="text" width={72} height={20} />
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default SecurityMasterSkeleton;
